import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from "./components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./components/ui/card";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";
import { Textarea } from "./components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./components/ui/select";
import { Checkbox } from "./components/ui/checkbox";
import { toast } from "sonner";
import { Building2, CheckCircle, AlertCircle } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AgenziaOnboarding = () => {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  
  const [formData, setFormData] = useState({
    nome_agenzia: '',
    zona_operativita: '',
    package_type: '',
    subscription_months: '',
    contatto_referente: '',
    telefono_referente: '',
    email_referente: '',
    note: '',
    consenso_gdpr: false
  });

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.nome_agenzia.trim() || !formData.zona_operativita.trim()) {
      toast.error('Nome agenzia e zona operatività sono obbligatori.');
      return;
    }

    if (!formData.package_type) {
      toast.error('Seleziona un pacchetto.');
      return;
    }

    if (!formData.subscription_months) {
      toast.error('Seleziona la durata dell\'abbonamento.');
      return;
    }

    if (!formData.consenso_gdpr) {
      toast.error('È necessario accettare il trattamento dei dati personali');
      return;
    }

    try {
      setSubmitting(true);
      
      // Map form data to backend schema
      const agenziaData = {
        nome: formData.nome_agenzia,
        email: formData.email_referente,
        password: formData.email_referente, // Temporary password, will be changed
        telefono: formData.telefono_referente,
        citta: formData.zona_operativita,
        descrizione: `Pacchetto: ${formData.package_type}, Durata: ${formData.subscription_months} mesi. ${formData.note ? 'Note: ' + formData.note : ''}`.trim(),
        zone_di_interesse: formData.zona_operativita
      };

      const response = await fetch(`${API}/v1/auth/agenzia/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(agenziaData)
      });

      if (response.ok) {
        const responseData = await response.json();
        toast.success(`🎉 Registrazione completata! La tua richiesta è in revisione.`);
        
        // Navigate to success page with pending status
        setTimeout(() => {
          navigate(`/onboarding-success?type=agenzia&status=pending`);
        }, 2000);
        
      } else {
        const errorData = await response.json();
        toast.error(`❌ ${errorData.detail || 'Errore durante la registrazione'}`);
      }
      
    } catch (error) {
      console.error('Registration error:', error);
      toast.error('❌ Errore di connessione. Riprova più tardi.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-emerald-50 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        <Card>
          <CardHeader className="text-center">
            <div className="mx-auto w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mb-4">
              <Building2 className="w-8 h-8 text-emerald-600" />
            </div>
            <CardTitle className="text-2xl text-gray-800">
              🏢 Registrazione Agenzia Immobiliare
            </CardTitle>
            <CardDescription>
              Completa il modulo per richiedere l'approvazione della tua agenzia su InLeads
            </CardDescription>
          </CardHeader>
          
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Nome Agenzia */}
              <div className="space-y-2">
                <Label htmlFor="nome_agenzia">Nome Agenzia *</Label>
                <Input
                  id="nome_agenzia"
                  value={formData.nome_agenzia}
                  onChange={(e) => handleInputChange('nome_agenzia', e.target.value)}
                  placeholder="Es: Immobiliare Milano Centro"
                  required
                />
              </div>

              {/* Zona Operatività */}
              <div className="space-y-2">
                <Label htmlFor="zona_operativita">Zona operatività *</Label>
                <Input
                  id="zona_operativita"
                  value={formData.zona_operativita}
                  onChange={(e) => handleInputChange('zona_operativita', e.target.value)}
                  placeholder="Es: Milano Centro, Roma Nord, Firenze..."
                  required
                />
              </div>

              {/* Pacchetti disponibili come card */}
              <div className="grid md:grid-cols-3 gap-6 mb-8">
                {[
                  {
                    key: 'Start Zone',
                    title: 'START ZONE',
                    price: '€160/mese per zona',
                    duration: 'Durata minima: 3 mesi',
                    color: 'blue',
                    features: [
                      '3 colloqui junior garantiti',
                      'Più colloqui possibili senza costi extra',
                      'Prova iniziale senza rischi'
                    ]
                  },
                  {
                    key: 'Flusso Attivo',
                    title: 'FLUSSO ATTIVO',
                    price: '€300/mese per zona',
                    duration: 'Durata minima: 3 mesi',
                    color: 'green',
                    features: [
                      '3 colloqui misti garantiti',
                      'Più colloqui possibili senza costi extra',
                      'Estensione gratuita se non si raggiunge il minimo',
                      'Per una crescita sostenuta'
                    ]
                  },
                  {
                    key: 'Team Esperto',
                    title: 'TEAM ESPERTO',
                    price: '€749/mese per zona',
                    duration: 'Durata minima: 3 mesi',
                    color: 'purple',
                    features: [
                      '3 colloqui senior garantiti',
                      'Più colloqui possibili senza costi extra',
                      'Estensione gratuita se non si raggiunge il minimo',
                      'Per agenzie strutturate'
                    ]
                  }
                ].map(pkg => (
                  <Card
                    key={pkg.key}
                    className={`shadow-lg border-${pkg.color}-200 cursor-pointer transition-all duration-200 ${formData.package_type === pkg.key ? `ring-2 ring-${pkg.color}-500 border-${pkg.color}-400` : ''}`}
                    onClick={() => handleInputChange('package_type', pkg.key)}
                  >
                    <CardHeader className={`bg-${pkg.color}-50 rounded-t-xl`}>
                      <CardTitle className={`text-${pkg.color}-700 text-lg`}>{pkg.title}</CardTitle>
                      <CardDescription className={`text-${pkg.color}-500`}>{pkg.price}<br/>{pkg.duration}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-2 text-sm text-gray-700">
                        {pkg.features.map((f, i) => (
                          <li key={i}>✔ {f}</li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Pacchetto Scelto */}
              <div className="space-y-2">
                <Label htmlFor="package_type">Pacchetto scelto *</Label>
                <Select 
                  value={formData.package_type} 
                  onValueChange={(value) => handleInputChange('package_type', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleziona un pacchetto" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Start Zone">
                      <div className="flex flex-col items-start text-left">
                        <span className="font-medium">Start Zone</span>
                        <span className="text-xs text-gray-500">Profili senza esperienza da formare</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="Flusso Attivo">
                      <div className="flex flex-col items-start text-left">
                        <span className="font-medium">Flusso Attivo</span>
                        <span className="text-xs text-gray-500">Profili con un minimo di esperienza nel settore</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="Team Esperto">
                      <div className="flex flex-col items-start text-left">
                        <span className="font-medium">Team Esperto</span>
                        <span className="text-xs text-gray-500">Profili con esperienza, minimo 3 anni nel settore</span>
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="subscription_months">Durata abbonamento (mesi) *</Label>
                <Select 
                  value={formData.subscription_months} 
                  onValueChange={(value) => handleInputChange('subscription_months', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleziona durata" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="2">2 mesi</SelectItem>
                    <SelectItem value="3">3 mesi</SelectItem>
                    <SelectItem value="6">6 mesi</SelectItem>
                    <SelectItem value="12">12 mesi</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Contatto Referente */}
              <div className="space-y-2">
                <Label htmlFor="contatto_referente">Nome referente</Label>
                <Input
                  id="contatto_referente"
                  value={formData.contatto_referente}
                  onChange={(e) => handleInputChange('contatto_referente', e.target.value)}
                  placeholder="Nome e cognome del responsabile"
                />
              </div>

              {/* Telefono */}
              <div className="space-y-2">
                <Label htmlFor="telefono_referente">Telefono referente</Label>
                <Input
                  id="telefono_referente"
                  value={formData.telefono_referente}
                  onChange={(e) => handleInputChange('telefono_referente', e.target.value)}
                  placeholder="+39 333 1234567"
                />
              </div>

              {/* Email */}
              <div className="space-y-2">
                <Label htmlFor="email_referente">Email referente *</Label>
                <Input
                  id="email_referente"
                  type="email"
                  value={formData.email_referente}
                  onChange={(e) => handleInputChange('email_referente', e.target.value)}
                  placeholder="referente@agenzia.it"
                  required
                />
              </div>

              {/* GDPR Consent */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex items-start space-x-2">
                  <input
                    type="checkbox"
                    id="consenso_gdpr"
                    checked={formData.consenso_gdpr}
                    onChange={(e) => handleInputChange('consenso_gdpr', e.target.checked)}
                    required
                    className="mt-1"
                  />
                  <Label htmlFor="consenso_gdpr" className="text-sm leading-relaxed">
                    Acconsento al trattamento dei miei dati personali secondo quanto previsto dal 
                    Regolamento UE 2016/679 (GDPR) per le finalità di selezione e recruiting. *
                  </Label>
                </div>
              </div>

              {/* Submit Button */}
              <div className="flex gap-4 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate('/registrati')}
                  className="flex-1"
                  disabled={submitting}
                >
                  Indietro
                </Button>
                <Button 
                  type="submit" 
                  disabled={submitting || !formData.consenso_gdpr}
                  className="flex-1 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700"
                >
                  {submitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Invio in corso...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Invia richiesta per approvazione
                    </>
                  )}
                </Button>
              </div>

              <div className="text-center text-sm text-blue-700 bg-blue-50 p-3 rounded-lg mt-4">
                ℹ️ La tua richiesta verrà revisionata dal team InLeads. 
                Riceverai le credenziali di accesso via email una volta approvata.
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AgenziaOnboarding;